/**
 * Site history (IDEAS X16): a system remembers its battles for the player. Nothing new is recorded — the battle
 * records, their chronicles, the scars and the sector log already hold it — this reads them as one record a
 * place carries: battles fought and who won, the decisive shot, who held it, what was built and destroyed, what
 * the garrison learned. Read through the viewer's knowledge: a battle is known to a faction that fought it, holds
 * the system now, or heard it (a loud battle is heard sector-wide); the log's own entries are the viewer's. Pure.
 */
import type { FactionId, SectorState, SystemId } from './types';
import { recapLines } from './chronicle';

export interface SiteEvent { day: number; kind: 'battle' | 'captured' | 'squadLost' | 'scout' | 'adapted' | 'order' | 'muster' | 'arrive' | 'other'; text: string }

/** Everything the viewer knows of a system's past, newest first. `viewer` null sees the truth. */
export function siteHistory(state: SectorState, systemId: SystemId, viewer: FactionId | null): SiteEvent[] {
  const sys = state.systems.find(s => s.id === systemId);
  if (!sys) return [];
  const out: SiteEvent[] = [];
  state.battles.forEach((b, i) => {
    if (b.setup.systemId !== systemId) return;
    const fought = viewer === null || b.setup.sides.some(s => s.faction === viewer);
    const known = fought || sys.owner === viewer || b.outcome.wentLoud;
    if (!known) return;
    const sides = b.setup.sides.slice(0, 2).map(s => `${s.faction} ${s.units.length}`).join(' v ');
    const length = b.outcome.clock === 'action' ? `${Math.round(b.outcome.seconds ?? 0)} s` : `${b.outcome.rounds} round${b.outcome.rounds === 1 ? '' : 's'}`;
    const dead = b.outcome.units.filter(u => !u.alive).length;
    const destroyed = (b.outcome.structuresSurvived ?? []).filter(x => x === false).length;
    let decisive = '';
    if (fought && b.outcome.chronicle?.length) {
      const names = new Map(b.setup.sides.flatMap(x => x.units.map(u => [u.unitId, u.name] as const)));
      const kills = b.outcome.chronicle.filter(e => e.kind === 'kill' || e.kind === 'down');
      if (kills.length) {
        const top = kills.reduce((a, e) => ((e.text?.match(/(\d+) kills/)?.[1] ?? 1) >= (a.text?.match(/(\d+) kills/)?.[1] ?? 1) ? e : a));
        decisive = `; decisive: ${recapLines([top], b.outcome.clock, id => names.get(id) ?? id)[0]}`;
      }
    }
    out.push({
      day: b.setup.day, kind: 'battle',
      text: `battle ${i + 1}: ${sides} — ${b.outcome.winner ? `${b.outcome.winner} won` : 'no decision'} (${b.outcome.reason ?? 'ended'}, ${length}); ${dead} fell${destroyed ? `, ${destroyed} structure${destroyed === 1 ? '' : 's'} destroyed` : ''}${b.outcome.reserveArrivals ? `, reserves arrived` : ''}${decisive}`,
    });
  });
  for (const e of state.log) {
    if (e.systemId !== systemId) continue;
    const mine = viewer === null || e.faction === viewer;
    if (e.kind === 'captured' && (mine || sys.owner === viewer || viewer === null || state.knowledge[viewer]?.systems[systemId])) out.push({ day: e.day, kind: 'captured', text: e.text });
    else if (mine && (e.kind === 'squadLost' || e.kind === 'scout' || e.kind === 'muster' || e.kind === 'arrive')) out.push({ day: e.day, kind: e.kind, text: e.text });
    else if (mine && e.kind === 'order' && /adapts|refits|takes on/.test(e.text)) out.push({ day: e.day, kind: /adapts/.test(e.text) ? 'adapted' : 'order', text: e.text });
  }
  if (sys.scars.battles > 0 && (viewer === null || sys.owner === viewer || state.knowledge[viewer]?.systems[systemId])) {
    out.push({ day: sys.lastBattleDay ?? 0, kind: 'other', text: `the ground: ${sys.scars.battles} battle${sys.scars.battles === 1 ? '' : 's'} fought here, ${sys.scars.craterVertices} craters, −${(sys.scars.massLostFraction * 100).toFixed(1)}% mass` });
  }
  return out.sort((a, b) => b.day - a.day || a.text.localeCompare(b.text));
}

/** The record as lines, newest first, at most `n`. */
export function siteHistoryLines(state: SectorState, systemId: SystemId, viewer: FactionId | null, n = 8): string[] {
  return siteHistory(state, systemId, viewer).slice(0, n).map(e => `day ${e.day.toFixed(1)} · ${e.text}`);
}
